import React, { useState, useEffect, useCallback } from 'react';
import { variationApi, projectApi, wbsApi } from '../services/api';
import { Card, Button, Input, Select, Modal, Badge, Spinner } from '../components/UI';
import { Plus, Trash, CheckCircle, Play, ArrowRight } from '@phosphor-icons/react';

import { fmtCurrencySigned } from '../utils/currencyFormat';

export default function VariationsPage() {
  const [variations, setVariations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProject, setFilterProject] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ project_id: '', activity_id: '', description: '', cost_impact: '', revenue_impact: '', reason: '' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([projectApi.list(), wbsApi.listActivities()])
      .then(([p, a]) => { setProjects(p.data); setActivities(a.data); })
      .finally(() => setLoading(false));
  }, []);

  const loadVariations = useCallback(async () => {
    const params = {};
    if (filterProject) params.project_id = filterProject;
    if (filterStatus) params.status = filterStatus;
    const res = await variationApi.list(params);
    setVariations(res.data);
  }, [filterProject, filterStatus]);

  useEffect(() => { loadVariations(); }, [loadVariations]);

  const fmt = (n) => fmtCurrencySigned(n, 0);

  const openCreate = () => {
    setForm({ project_id: filterProject || '', activity_id: '', description: '', cost_impact: '', revenue_impact: '', reason: '' });
    setError(''); setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); setError(''); setSaving(true);
    try {
      await variationApi.create({
        ...form, cost_impact: parseFloat(form.cost_impact) || 0, revenue_impact: parseFloat(form.revenue_impact) || 0
      });
      setShowModal(false); loadVariations();
    } catch (err) { setError(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Approve this variation? This will update project financials and create journal entries.')) return;
    try { await variationApi.approve(id); loadVariations(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleExecute = async (id) => {
    if (!window.confirm('Mark this variation as executed?')) return;
    try { await variationApi.execute(id); loadVariations(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this draft variation?')) return;
    try { await variationApi.delete(id); loadVariations(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const statusVariant = (s) => s === 'executed' ? 'success' : s === 'approved' ? 'warning' : 'default';

  if (loading) return <div className="flex justify-center py-8"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-4" data-testid="variations-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#0A0A0A]">Variations</h1>
          <p className="text-sm text-[#666]">Change orders: Create → Approve → Execute</p>
        </div>
        <Button onClick={openCreate} data-testid="add-variation-btn"><Plus className="w-4 h-4 mr-2" /> New Variation</Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Select value={filterProject} onChange={(e) => setFilterProject(e.target.value)} data-testid="filter-project">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </Select>
        <Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="filter-status">
          <option value="">All Statuses</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="executed">Executed</option>
        </Select>
      </div>

      {variations.length === 0 ? (
        <Card className="p-8 text-center text-[#666]">No variations</Card>
      ) : (
        <div className="space-y-2">
          {variations.map(v => (
            <Card key={v.id} className="p-3 sm:p-4" data-testid={`variation-row-${v.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant={statusVariant(v.status)}>{v.status}</Badge>
                    <span className="font-semibold text-sm">{v.description}</span>
                  </div>
                  <div className="flex flex-wrap gap-3 mt-1 text-xs text-[#666]">
                    <span>{v.project_name}</span>
                    <span>{v.activity_name}</span>
                    {v.reason && <span className="italic">{v.reason}</span>}
                  </div>
                  <div className="flex gap-4 mt-1.5 text-xs">
                    <span>Cost: <b className={v.cost_impact > 0 ? 'text-[#FF3333]' : v.cost_impact < 0 ? 'text-[#00C853]' : ''}>{fmt(v.cost_impact)}</b></span>
                    <span>Revenue: <b className={v.revenue_impact > 0 ? 'text-[#00C853]' : v.revenue_impact < 0 ? 'text-[#FF3333]' : ''}>{fmt(v.revenue_impact)}</b></span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {v.status === 'draft' && (
                    <>
                      <Button size="sm" onClick={() => handleApprove(v.id)} data-testid={`approve-variation-${v.id}`}>
                        <CheckCircle className="w-3 h-3 mr-1" /> Approve
                      </Button>
                      <button onClick={() => handleDelete(v.id)} className="p-1 text-[#666] hover:text-[#FF3333]" data-testid={`delete-variation-${v.id}`}><Trash className="w-4 h-4" /></button>
                    </>
                  )}
                  {v.status === 'approved' && (
                    <Button size="sm" variant="secondary" onClick={() => handleExecute(v.id)} data-testid={`execute-variation-${v.id}`}>
                      <Play className="w-3 h-3 mr-1" /> Execute
                    </Button>
                  )}
                  {v.status === 'executed' && <CheckCircle className="w-5 h-5 text-[#00C853]" weight="fill" />}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="New Variation">
        <form onSubmit={handleSubmit} className="space-y-3" data-testid="variation-form">
          {error && <div className="p-2 bg-[#FF3333]/10 text-[#FF3333] rounded text-sm">{error}</div>}
          <Select label="Project" value={form.project_id} onChange={(e) => setForm({ ...form, project_id: e.target.value })} required data-testid="var-project">
            <option value="">-- Select --</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </Select>
          <Select label="Activity" value={form.activity_id} onChange={(e) => setForm({ ...form, activity_id: e.target.value })} required data-testid="var-activity">
            <option value="">-- Select --</option>
            {activities.map(a => <option key={a.id} value={a.id}>{a.trade} / {a.activity_name}</option>)}
          </Select>
          <Input label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required placeholder="What changed" data-testid="var-desc" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Cost Impact ($)" type="number" step="any" value={form.cost_impact} onChange={(e) => setForm({ ...form, cost_impact: e.target.value })} data-testid="var-cost" />
            <Input label="Revenue Impact ($)" type="number" step="any" value={form.revenue_impact} onChange={(e) => setForm({ ...form, revenue_impact: e.target.value })} data-testid="var-revenue" />
          </div>
          <Input label="Reason" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Why this change" data-testid="var-reason" />
          <div className="flex justify-end space-x-3 pt-3">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="save-variation-btn">{saving ? 'Saving...' : 'Create Draft'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
